import { all, put, take, takeLatest, race, select, call } from 'redux-saga/effects'

import { startLoading, finishLoading } from 'reducers/layoutSlice'
import {
  actions as shopsActions,
  formatters as shopsFormatters,
  selectors as shopsSelectors,
} from 'domains/shops'
import * as actions from 'actions/views/shops'
import { FIELD_NAMES } from 'const/forms'
import { message } from 'antd'
import {
  formatCampaignForUpdate,
  formatShopOfferForRequests,
  formatPartnerOfferForRequest,
  formatConquestAdForRequest,
} from 'domains/shops/formatters'
import { SHOP_SUB_ENTITIES } from '../../domains/shops/const'
import * as shopsApi from 'domains/shops/api'

function* fetchShopsWorker({ payload } = {}) {
  try {
    const search = yield select(shopsSelectors.getSearchString)
    const body = shopsFormatters.formatPayloadForFetchRequest({
      search,
      ...payload,
    })
    yield all([put(startLoading()), put(shopsActions.fetchShops(body))])
  } catch (error) {
    console.error(error)
  } finally {
    yield put(finishLoading())
  }
}

function* fetchLocalizedShopsWorker({ payload } = {}) {
  try {
    const search = yield select(shopsSelectors.getLocalizedShops)
    const body = shopsFormatters.formatPayloadForFetchLocalizedShopsRequest({
      search,
      ...payload,
    })
    yield put(shopsActions.fetchLocalizedShops(body))

    const [success] = yield race([
      take(shopsActions.fetchLocalizedShopsSuccess),
      take(shopsActions.fetchLocalizedShopsFailure),
    ])

    if (success) {
      yield put(
        shopsActions.saveFiltersAndSorter({
          ...payload,
          category: SHOP_SUB_ENTITIES.localizedShops,
        })
      )
    }
  } catch (error) {
    console.error(error)
  }
}

function* fetchShopImageSetsWorker() {
  try {
    const id = yield select(shopsSelectors.getCurrentShopId)
    yield put(shopsActions.fetchShopImageSets({ id }))
  } catch (error) {
    console.error(error)
  }
}

function* fetchShopCampaignDealsWorker({ payload }) {
  try {
    const id = yield select(shopsSelectors.getCurrentShopId)
    yield all([put(shopsActions.fetchShopCampaignDeals({ id, ...payload }))])

    const [success] = yield race([
      take(shopsActions.fetchShopCampaignDealsSuccess),
      take(shopsActions.fetchShopCampaignDealsFailure),
    ])

    if (success) {
      yield put(
        shopsActions.saveFiltersAndSorter({
          ...payload,
          category: SHOP_SUB_ENTITIES.campaignDeals,
        })
      )
    }
  } catch (error) {
    console.error(error)
  }
}

function* fetchShopOffersWorker({ payload }) {
  try {
    const id = yield select(shopsSelectors.getCurrentShopId)
    yield all([put(shopsActions.fetchShopOffers({ id, ...payload }))])

    const [success] = yield race([
      take(shopsActions.fetchShopOffersSuccess),
      take(shopsActions.fetchShopOffersFailure),
    ])

    if (success) {
      yield put(
        shopsActions.saveFiltersAndSorter({
          ...payload,
          category: SHOP_SUB_ENTITIES.shopOffers,
        })
      )
    }
  } catch (error) {
    console.error(error)
  }
}

function* fetchShopAdPackagesWorker({ payload }) {
  try {
    const id = yield select(shopsSelectors.getCurrentShopId)
    yield all([put(shopsActions.fetchShopAdPackages({ id, ...payload }))])

    const [success] = yield race([
      take(shopsActions.fetchShopAdPackagesSuccess),
      take(shopsActions.fetchShopAdPackagesFailure),
    ])

    if (success) {
      yield put(
        shopsActions.saveFiltersAndSorter({
          ...payload,
          category: SHOP_SUB_ENTITIES.adPackages,
        })
      )
    }
  } catch (error) {
    console.error(error)
  }
}

function* fetchShopProgramsWorker({ payload }) {
  try {
    const id = yield select(shopsSelectors.getCurrentShopId)
    yield put(shopsActions.fetchShopPrograms({ id, ...payload }))

    yield take([
      shopsActions.fetchShopProgramsSuccess,
      shopsActions.fetchShopProgramsFailure,
    ])
  } catch (error) {
    console.error(error)
  } finally {
    yield put(finishLoading())
  }
}

function* updateShopWorker({ payload }) {
  try {
    const body = shopsFormatters.formatShopsForUpdate(payload.values)
    yield all([
      put(shopsActions.startSaving()),
      put(
        shopsActions.updateShop({
          ...body,
          id: payload.values[FIELD_NAMES.id],
        })
      ),
    ])
    const [success] = yield race([
      take(shopsActions.updateShopSuccess),
      take(shopsActions.updateShopFailure),
    ])

    if (success) {
      put(shopsActions.fetchLocalizedShops(payload.values[FIELD_NAMES.id]))
      message.success('Shop updated')
      payload.callback()
    }
  } catch (error) {
    console.error(error)
  } finally {
    yield put(shopsActions.finishSaving())
  }
}

function* updateLocalizedShopWorker({ payload }) {
  try {
    const body = shopsFormatters.formatLocalizedShopsForUpdate(payload.values)
    yield all([
      put(shopsActions.startSaving()),
      put(
        shopsActions.updateLocalizedShop({
          data: body,
          id: payload.values[FIELD_NAMES.id],
        })
      ),
    ])
    const [success] = yield race([
      take(shopsActions.updateLocalizedShopSuccess),
      take(shopsActions.updateLocalizedShopFailure),
    ])

    if (success) {
      const search = yield select(shopsSelectors.getCurrentShopId)
      yield put(actions.fetchLocalizedShops({ search: search }))
      message.success('Localized shop updated')
      payload.callback()
    }
  } catch (error) {
    console.error(error)
  } finally {
    yield put(shopsActions.finishSaving())
  }
}

function* updateShopCouponsSettingsWorker({ payload }) {
  try {
    yield all([
      put(shopsActions.startSaving()),
      put(shopsActions.updateShopCouponsSettings(payload)),
    ])
    const [success] = yield race([
      take(shopsActions.updateShopCouponsSettingsSuccess),
      take(shopsActions.updateShopCouponsSettingsFailure),
    ])

    if (success) {
      const search = yield select(shopsSelectors.getCurrentShopId)
      yield put(actions.fetchLocalizedShops({ search: search }))
      message.success('Localized shop updated')
      payload.callback()
    }
  } catch (error) {
    console.error(error)
  } finally {
    yield put(shopsActions.finishSaving())
  }
}

function* createCampaignWorker({ payload }) {
  try {
    const body = formatCampaignForUpdate(payload.data)
    yield all([
      put(shopsActions.startSaving()),
      put(shopsActions.createShopCampaign(body)),
    ])
    const [success] = yield race([
      take(shopsActions.createShopCampaignSuccess),
      take(shopsActions.createShopCampaignFailure),
    ])

    if (success) {
      const [filters, sorter, currentShopId] = yield all([
        select(shopsSelectors.getCampaignDealsFilters),
        select(shopsSelectors.getCampaignDealsSorter),
        select(shopsSelectors.getCurrentShopId),
      ])
      if (currentShopId) {
        yield put(
          actions.fetchShopCampaignDeals({ currentShopId, filters, sorter })
        )
      }
      payload.callback()
    }
  } catch (error) {
    console.error(error)
  } finally {
    yield put(shopsActions.finishSaving())
  }
}

function* updateCampaignWorker({ payload }) {
  try {
    const body = formatCampaignForUpdate(payload.data)
    yield all([
      put(shopsActions.startSaving()),
      put(
        shopsActions.updateShopCampaign({
          data: body,
          id: payload.data[FIELD_NAMES.id],
        })
      ),
    ])
    const [success] = yield race([
      take(shopsActions.updateShopCampaignSuccess),
      take(shopsActions.updateShopCampaignFailure),
    ])

    if (success) {
      const [page, filters, sorter, currentShopId] = yield all([
        select(shopsSelectors.getCampaignDealsPage),
        select(shopsSelectors.getCampaignDealsFilters),
        select(shopsSelectors.getCampaignDealsSorter),
        select(shopsSelectors.getCurrentShopId),
      ])
      if (currentShopId) {
        yield put(
          actions.fetchShopCampaignDeals({
            currentShopId,
            page,
            filters,
            sorter,
          })
        )
      }
      payload.callback()
    }
  } catch (error) {
    console.error(error)
  } finally {
    yield put(shopsActions.finishSaving())
  }
}

function* createShopOfferWorker({ payload }) {
  try {
    const shopId = yield select(shopsSelectors.getCurrentShopId)
    const body = formatShopOfferForRequests({
      ...payload.values,
      shop_id: shopId,
    })
    yield all([
      put(shopsActions.startSaving()),
      put(shopsActions.createShopOffer(body)),
    ])
    const [success] = yield race([
      take(shopsActions.createShopOfferSuccess),
      take(shopsActions.createShopOfferFailure),
    ])

    if (success) {
      const [filters, sorter] = yield all([
        select(shopsSelectors.getShopOffersFilters),
        select(shopsSelectors.getShopOffersSorter),
      ])
      yield put(
        shopsActions.fetchShopOffers({
          id: shopId,
          filters,
          sorter,
        })
      )
      payload.callback()
    }
  } catch (error) {
    console.error(error)
  } finally {
    yield put(shopsActions.finishSaving())
  }
}

function* updateShopOfferWorker({ payload }) {
  try {
    const body = formatShopOfferForRequests(payload.values)
    yield all([
      put(shopsActions.startSaving()),
      put(shopsActions.updateShopOffer(body)),
    ])
    const [success] = yield race([
      take(shopsActions.updateShopOfferSuccess),
      take(shopsActions.updateShopOfferFailure),
    ])
    if (success) {
      const [filters, sorter, currentShopId] = yield all([
        select(shopsSelectors.getShopOffersFilters),
        select(shopsSelectors.getShopOffersSorter),
        select(shopsSelectors.getCurrentShopId),
      ])
      yield put(
        shopsActions.fetchShopOffers({
          id: currentShopId,
          filters,
          sorter,
        })
      )
      payload.callback()
    }
  } catch (error) {
    console.error(error)
  } finally {
    yield put(shopsActions.finishSaving())
  }
}

function* deleteCampaignWorker({ payload }) {
  try {
    yield all([
      put(shopsActions.startSaving()),
      put(shopsActions.deleteCampaign(payload)),
    ])
    const [success] = yield race([
      take(shopsActions.deleteCampaignSuccess),
      take(shopsActions.deleteCampaignFailure),
    ])

    if (success) {
      const [page, filters, sorter, currentShopId] = yield all([
        select(shopsSelectors.getCampaignDealsPage),
        select(shopsSelectors.getCampaignDealsFilters),
        select(shopsSelectors.getCampaignDealsSorter),
        select(shopsSelectors.getCurrentShopId),
      ])
      yield put(
        shopsActions.fetchShopCampaignDeals({
          page,
          filters,
          sorter,
          id: currentShopId,
        })
      )
    }
  } catch (error) {
    console.error(error)
  } finally {
    yield put(finishLoading())
  }
}

function* deleteAdPackageWorker({ payload }) {
  try {
    yield all([
      put(shopsActions.startSaving()),
      put(shopsActions.deleteAdPackage(payload)),
    ])
    const [success] = yield race([
      take(shopsActions.deleteAdPackageSuccess),
      take(shopsActions.deleteAdPackageFailure),
    ])

    if (success) {
      const [page, currentShopId] = yield all([
        select(shopsSelectors.getAdPackagesPage),
        select(shopsSelectors.getCurrentShopId),
      ])
      yield put(
        shopsActions.fetchShopAdPackages({
          page,
          id: currentShopId,
        })
      )
    }
  } catch (error) {
    console.error(error)
  } finally {
    yield put(shopsActions.finishSaving())
  }
}

function* duplicateAdPackageWorker({ payload }) {
  try {
    yield all([
      put(shopsActions.startSaving()),
      put(shopsActions.duplicateAdPackage(payload)),
    ])
    const [success] = yield race([
      take(shopsActions.duplicateAdPackageSuccess),
      take(shopsActions.duplicateAdPackageFailure),
    ])

    if (success) {
      const [page, currentShopId] = yield all([
        select(shopsSelectors.getAdPackagesPage),
        select(shopsSelectors.getCurrentShopId),
      ])
      yield put(
        shopsActions.fetchShopAdPackages({
          page,
          id: currentShopId,
        })
      )
    }
  } catch (error) {
    console.error(error)
  } finally {
    yield put(shopsActions.finishSaving())
  }
}

function* deleteShopOfferWorker({ payload }) {
  try {
    yield all([
      put(shopsActions.startSaving()),
      put(shopsActions.deleteShopOffer(payload)),
    ])
    const [success] = yield race([
      take(shopsActions.deleteShopOfferSuccess),
      take(shopsActions.deleteShopOfferFailure),
    ])

    if (success) {
      const [page, currentShopId] = yield all([
        select(shopsSelectors.getShopOffersPage),
        select(shopsSelectors.getCurrentShopId),
      ])
      yield put(
        shopsActions.fetchShopOffers({
          page,
          id: currentShopId,
        })
      )
    }
  } catch (error) {
    console.error(error)
  } finally {
    yield put(finishLoading())
  }
}

function* deleteShopAllOffersWorker({ payload }) {
  try {
    yield all([
      put(shopsActions.startSaving()),
      put(shopsActions.deleteShopAllOffers(payload)),
    ])
    const [success] = yield race([
      take(shopsActions.deleteShopAllOffersSuccess),
      take(shopsActions.deleteShopAllOffersFailure),
    ])

    if (success) {
      const [ currentShopId] = yield all([
        select(shopsSelectors.getCurrentShopId),
      ])
      yield put(shopsActions.fetchShopOffers({ id: currentShopId, page: 1 }))
    }
  } catch (error) {
    console.error(error)
  } finally {
    yield put(finishLoading())
  }
}

function* createShopGroupWorker({ payload }) {
  try {
    const shopId = yield select(shopsSelectors.getCurrentShopId)
    const body = shopsFormatters.formatShopGroupForCreate(
      shopId,
      payload.values
    )
    yield put(shopsActions.createShopGroup(body))

    const [success] = yield race([
      take(shopsActions.createShopGroupSuccess),
      take(shopsActions.createShopGroupFailure),
    ])

    if (success) {
      yield put(actions.fetchLocalizedShops({ search: shopId }))
    }
  } catch (error) {
    console.error(error)
  }
}

function* updateShopGroupWorker({ payload }) {
  try {
    const body = shopsFormatters.formatShopGroupForUpdate(payload.values)
    yield put(shopsActions.updateShopGroup(body))

    const [success] = yield race([
      take(shopsActions.updateShopGroupSuccess),
      take(shopsActions.updateShopGroupFailure),
    ])

    if (success) {
      const shopId = yield select(shopsSelectors.getCurrentShopId)
      yield put(actions.fetchLocalizedShops({ search: shopId }))
    }
  } catch (error) {
    console.error(error)
  }
}

function* deleteShopGroupWorker({ payload }) {
  try {
    yield put(shopsActions.deleteShopGroup(payload))

    const [success] = yield race([
      take(shopsActions.deleteShopGroupSuccess),
      take(shopsActions.deleteShopGroupFailure),
    ])

    if (success) {
      const shopId = yield select(shopsSelectors.getCurrentShopId)
      yield put(actions.fetchLocalizedShops({ search: shopId }))
    }
  } catch (error) {
    console.error(error)
  }
}

function* createShopCategoryWorker({ payload }) {
  try {
    const shopId = yield select(shopsSelectors.getCurrentShopId)
    const body = shopsFormatters.formatShopCategoryForCreate(
      shopId,
      payload.values.categoryId,
      payload.values.main
    )

    yield put(shopsActions.createShopCategory(body))

    const [success] = yield race([
      take(shopsActions.createShopCategorySuccess),
      take(shopsActions.createShopCategoryFailure),
    ])

    if (success) {
      yield put(actions.fetchLocalizedShops({ search: shopId }))
    }
  } catch (error) {
    console.error(error)
  }
}

function* updateShopCategoryWorker({ payload }) {
  try {
    const body = shopsFormatters.formatShopCategoryForUpdate(payload.values)

    yield put(shopsActions.updateShopCategory(body))

    const [success] = yield race([
      take(shopsActions.updateShopCategorySuccess),
      take(shopsActions.updateShopCategoryFailure),
    ])

    if (success) {
      const shopId = yield select(shopsSelectors.getCurrentShopId)
      yield put(actions.fetchLocalizedShops({ search: shopId }))
    }
  } catch (error) {
    console.error(error)
  }
}

function* deleteShopCategoryWorker({ payload }) {
  try {
    yield put(shopsActions.deleteShopCategory(payload))

    const [success] = yield race([
      take(shopsActions.deleteShopCategorySuccess),
      take(shopsActions.deleteShopCategoryFailure),
    ])

    if (success) {
      const shopId = yield select(shopsSelectors.getCurrentShopId)
      yield put(actions.fetchLocalizedShops({ search: shopId }))
    }
  } catch (error) {
    console.error(error)
  }
}

function* fetchUpdateTagsCountWorker({ payload }) {
  try {
    const { id } = payload
    yield all([
      put(startLoading()),
      put(shopsActions.startSaving()),
    ])
    const response = yield call(shopsApi.fetchUpdateTagsCount, { id })
    if (response && response.data) {
      yield put(actions.fetchLocalizedShops({ search: id }))
    }
  } catch (error) {
    console.error(error)
  } finally {
    yield put(finishLoading())
    yield put(shopsActions.finishSaving())
  }
}

// SEO Descriptions workers
function* fetchShopSeoDescriptionsWorker({ payload }) {
  try {
    const id = yield select(shopsSelectors.getCurrentShopId)
    yield all([put(shopsActions.fetchShopSeoDescriptions({ id, ...payload }))])

    const [success] = yield race([
      take(shopsActions.fetchShopSeoDescriptionsSuccess),
      take(shopsActions.fetchShopSeoDescriptionsFailure),
    ])

    if (success) {
      yield put(
        shopsActions.saveFiltersAndSorter({
          ...payload,
          category: SHOP_SUB_ENTITIES.seoDescriptions,
        })
      )
    }
  } catch (error) {
    console.error(error)
  }
}

function* createShopSeoDescriptionWorker({ payload }) {
  try {
    const shopId = yield select(shopsSelectors.getCurrentShopId)
    const body = {
      shop_id: shopId,
      title: payload.values.title,
      description: payload.values.description,
      image_url: payload.values.image_url,
      order_value: payload.values.order_value,
    }
    yield all([
      put(shopsActions.startSaving()),
      put(shopsActions.createShopSeoDescription(body)),
    ])
    const [success] = yield race([
      take(shopsActions.createShopSeoDescriptionSuccess),
      take(shopsActions.createShopSeoDescriptionFailure),
    ])

    if (success) {
      const [filters, sorter] = yield all([
        select(shopsSelectors.getShopSeoDescriptionsFilters),
        select(shopsSelectors.getShopSeoDescriptionsSorter),
      ])
      yield put(
        shopsActions.fetchShopSeoDescriptions({
          id: shopId,
          filters,
          sorter,
        })
      )
      message.success('SEO description created')
      payload.callback()
    }
  } catch (error) {
    console.error(error)
  } finally {
    yield put(shopsActions.finishSaving())
  }
}

function* updateShopSeoDescriptionWorker({ payload }) {
  try {
    const body = {
      id: payload.values.id,
      title: payload.values.title,
      description: payload.values.description,
      image_url: payload.values.image_url,
      order_value: payload.values.order_value,
    }
    yield all([
      put(shopsActions.startSaving()),
      put(shopsActions.updateShopSeoDescription(body)),
    ])
    const [success] = yield race([
      take(shopsActions.updateShopSeoDescriptionSuccess),
      take(shopsActions.updateShopSeoDescriptionFailure),
    ])
    if (success) {
      const [filters, sorter, currentShopId] = yield all([
        select(shopsSelectors.getShopSeoDescriptionsFilters),
        select(shopsSelectors.getShopSeoDescriptionsSorter),
        select(shopsSelectors.getCurrentShopId),
      ])
      yield put(
        shopsActions.fetchShopSeoDescriptions({
          id: currentShopId,
          filters,
          sorter,
        })
      )
      message.success('SEO description updated')
      payload.callback()
    }
  } catch (error) {
    console.error(error)
  } finally {
    yield put(shopsActions.finishSaving())
  }
}

function* deleteShopSeoDescriptionWorker({ payload }) {
  try {
    yield all([
      put(shopsActions.startSaving()),
      put(shopsActions.deleteShopSeoDescription(payload)),
    ])
    const [success] = yield race([
      take(shopsActions.deleteShopSeoDescriptionSuccess),
      take(shopsActions.deleteShopSeoDescriptionFailure),
    ])

    if (success) {
      const [page, currentShopId] = yield all([
        select(shopsSelectors.getShopSeoDescriptionsPage),
        select(shopsSelectors.getCurrentShopId),
      ])
      yield put(
        shopsActions.fetchShopSeoDescriptions({
          page,
          id: currentShopId,
        })
      )
      message.success('SEO description deleted')
    }
  } catch (error) {
    console.error(error)
  } finally {
    yield put(shopsActions.finishSaving())
  }
}

// Partner Offers workers
function* fetchPartnerOffersWorker({ payload }) {
  try {
    const shopId = yield select(shopsSelectors.getCurrentShopId)
    yield put(shopsActions.fetchPartnerOffers({ shopId, ...payload }))

    const [success] = yield race([
      take(shopsActions.fetchPartnerOffersSuccess),
      take(shopsActions.fetchPartnerOffersFailure),
    ])

    if (success) {
      yield put(
        shopsActions.saveFiltersAndSorter({
          ...payload,
          category: SHOP_SUB_ENTITIES.partnerOffers,
        })
      )
    }
  } catch (error) {
    console.error(error)
  }
}

function* createPartnerOfferWorker({ payload }) {
  try {
    const body = formatPartnerOfferForRequest(payload.values, true)
    yield all([
      put(shopsActions.startSaving()),
      put(shopsActions.createPartnerOffer(body)),
    ])
    const [success] = yield race([
      take(shopsActions.createPartnerOfferSuccess),
      take(shopsActions.createPartnerOfferFailure),
    ])

    if (success) {
      const shopId = yield select(shopsSelectors.getCurrentShopId)
      const [filters, sorter] = yield all([
        select(shopsSelectors.getPartnerOffersFilters),
        select(shopsSelectors.getPartnerOffersSorter),
      ])
      yield put(
        shopsActions.fetchPartnerOffers({ shopId, filters, sorter })
      )
      message.success('Partner offer created')
      payload.callback()
    }
  } catch (error) {
    console.error(error)
  } finally {
    yield put(shopsActions.finishSaving())
  }
}

function* updatePartnerOfferWorker({ payload }) {
  try {
    const body = formatPartnerOfferForRequest(payload.values, false)
    yield all([
      put(shopsActions.startSaving()),
      put(
        shopsActions.updatePartnerOffer({
          id: payload.values[FIELD_NAMES.id],
          data: body,
        })
      ),
    ])
    const [success] = yield race([
      take(shopsActions.updatePartnerOfferSuccess),
      take(shopsActions.updatePartnerOfferFailure),
    ])

    if (success) {
      const shopId = yield select(shopsSelectors.getCurrentShopId)
      const [filters, sorter] = yield all([
        select(shopsSelectors.getPartnerOffersFilters),
        select(shopsSelectors.getPartnerOffersSorter),
      ])
      yield put(
        shopsActions.fetchPartnerOffers({ shopId, filters, sorter })
      )
      message.success('Partner offer updated')
      payload.callback()
    }
  } catch (error) {
    console.error(error)
  } finally {
    yield put(shopsActions.finishSaving())
  }
}

function* deletePartnerOfferWorker({ payload }) {
  try {
    yield all([
      put(shopsActions.startSaving()),
      put(shopsActions.deletePartnerOffer(payload)),
    ])
    const [success] = yield race([
      take(shopsActions.deletePartnerOfferSuccess),
      take(shopsActions.deletePartnerOfferFailure),
    ])

    if (success) {
      const [page, shopId] = yield all([
        select(shopsSelectors.getPartnerOffersPage),
        select(shopsSelectors.getCurrentShopId),
      ])
      yield put(
        shopsActions.fetchPartnerOffers({ page, shopId })
      )
      message.success('Partner offer deleted')
    }
  } catch (error) {
    console.error(error)
  } finally {
    yield put(shopsActions.finishSaving())
  }
}

// Conquest Ads workers
function* fetchConquestAdsWorker({ payload }) {
  try {
    const shopId = yield select(shopsSelectors.getCurrentShopId)
    yield put(shopsActions.fetchConquestAds({ shopId, ...payload }))

    const [success] = yield race([
      take(shopsActions.fetchConquestAdsSuccess),
      take(shopsActions.fetchConquestAdsFailure),
    ])

    if (success) {
      yield put(
        shopsActions.saveFiltersAndSorter({
          ...payload,
          category: SHOP_SUB_ENTITIES.conquestAds,
        })
      )
    }
  } catch (error) {
    console.error(error)
  }
}

function* createConquestAdWorker({ payload }) {
  try {
    const shopId = yield select(shopsSelectors.getCurrentShopId)
    const body = formatConquestAdForRequest(payload.values, true, shopId)
    yield all([
      put(shopsActions.startSaving()),
      put(shopsActions.createConquestAd(body)),
    ])
    const [success] = yield race([
      take(shopsActions.createConquestAdSuccess),
      take(shopsActions.createConquestAdFailure),
    ])

    if (success) {
      const [filters, sorter] = yield all([
        select(shopsSelectors.getConquestAdsFilters),
        select(shopsSelectors.getConquestAdsSorter),
      ])
      yield put(
        shopsActions.fetchConquestAds({ shopId, filters, sorter })
      )
      message.success('Conquest ad created')
      payload.callback()
    }
  } catch (error) {
    console.error(error)
  } finally {
    yield put(shopsActions.finishSaving())
  }
}

function* updateConquestAdWorker({ payload }) {
  try {
    const shopId = yield select(shopsSelectors.getCurrentShopId)
    const body = formatConquestAdForRequest(payload.values, false, shopId)
    yield all([
      put(shopsActions.startSaving()),
      put(
        shopsActions.updateConquestAd({
          id: payload.values[FIELD_NAMES.id],
          data: body,
        })
      ),
    ])
    const [success] = yield race([
      take(shopsActions.updateConquestAdSuccess),
      take(shopsActions.updateConquestAdFailure),
    ])

    if (success) {
      const [filters, sorter] = yield all([
        select(shopsSelectors.getConquestAdsFilters),
        select(shopsSelectors.getConquestAdsSorter),
      ])
      yield put(
        shopsActions.fetchConquestAds({ shopId, filters, sorter })
      )
      message.success('Conquest ad updated')
      payload.callback()
    }
  } catch (error) {
    console.error(error)
  } finally {
    yield put(shopsActions.finishSaving())
  }
}

function* deleteConquestAdWorker({ payload }) {
  try {
    yield all([
      put(shopsActions.startSaving()),
      put(shopsActions.deleteConquestAd(payload)),
    ])
    const [success] = yield race([
      take(shopsActions.deleteConquestAdSuccess),
      take(shopsActions.deleteConquestAdFailure),
    ])

    if (success) {
      const [page, shopId] = yield all([
        select(shopsSelectors.getConquestAdsPage),
        select(shopsSelectors.getCurrentShopId),
      ])
      yield put(
        shopsActions.fetchConquestAds({ page, shopId })
      )
      message.success('Conquest ad deleted')
    }
  } catch (error) {
    console.error(error)
  } finally {
    yield put(shopsActions.finishSaving())
  }
}

export default function* () {
  yield all([
    takeLatest(actions.fetchShops, fetchShopsWorker),
    takeLatest(actions.fetchLocalizedShops, fetchLocalizedShopsWorker),
    takeLatest(actions.updateShop, updateShopWorker),
    takeLatest(actions.updateLocalizedShop, updateLocalizedShopWorker),
    takeLatest(
      actions.updateShopCouponsSettings,
      updateShopCouponsSettingsWorker
    ),
    takeLatest(actions.fetchShopCampaignDeals, fetchShopCampaignDealsWorker),
    takeLatest(actions.fetchShopImageSets, fetchShopImageSetsWorker),
    takeLatest(actions.fetchShopAdPackages, fetchShopAdPackagesWorker),
    takeLatest(actions.fetchShopPrograms, fetchShopProgramsWorker),
    takeLatest(actions.updateShopCampaign, updateCampaignWorker),
    takeLatest(actions.createShopCampaign, createCampaignWorker),
    takeLatest(actions.fetchShopOffers, fetchShopOffersWorker),
    takeLatest(actions.createShopOffer, createShopOfferWorker),
    takeLatest(actions.updateShopOffer, updateShopOfferWorker),
    takeLatest(actions.deleteCampaign, deleteCampaignWorker),
    takeLatest(actions.deleteAdPackage, deleteAdPackageWorker),
    takeLatest(actions.duplicateAdPackage, duplicateAdPackageWorker),
    takeLatest(actions.deleteShopOffer, deleteShopOfferWorker),
    takeLatest(actions.deleteShopAllOffers, deleteShopAllOffersWorker),
    takeLatest(actions.createShopGroup, createShopGroupWorker),
    takeLatest(actions.updateShopGroup, updateShopGroupWorker),
    takeLatest(actions.deleteShopGroup, deleteShopGroupWorker),
    takeLatest(actions.createShopCategory, createShopCategoryWorker),
    takeLatest(actions.updateShopCategory, updateShopCategoryWorker),
    takeLatest(actions.deleteShopCategory, deleteShopCategoryWorker),
    takeLatest(actions.fetchUpdateTagsCount, fetchUpdateTagsCountWorker),
    takeLatest(actions.fetchShopSeoDescriptions, fetchShopSeoDescriptionsWorker),
    takeLatest(actions.createShopSeoDescription, createShopSeoDescriptionWorker),
    takeLatest(actions.updateShopSeoDescription, updateShopSeoDescriptionWorker),
    takeLatest(actions.deleteShopSeoDescription, deleteShopSeoDescriptionWorker),
    takeLatest(actions.fetchPartnerOffers, fetchPartnerOffersWorker),
    takeLatest(actions.createPartnerOffer, createPartnerOfferWorker),
    takeLatest(actions.updatePartnerOffer, updatePartnerOfferWorker),
    takeLatest(actions.deletePartnerOffer, deletePartnerOfferWorker),
    takeLatest(actions.fetchConquestAds, fetchConquestAdsWorker),
    takeLatest(actions.createConquestAd, createConquestAdWorker),
    takeLatest(actions.updateConquestAd, updateConquestAdWorker),
    takeLatest(actions.deleteConquestAd, deleteConquestAdWorker),
  ])
}
